import React, { useCallback, useEffect, useState } from 'react';
import { Divider, Select, Spin } from 'antd';
import { useHistory } from 'react-router-dom';
import * as _ from 'lodash';
import QuoteStore from '../stores/QuoteStore';
import SalesOrderStore from '../stores/SalesOrderStore';
import { IInvoice, QuoteProps, SalesOrderProps } from '../types/types';
import { LabeledValue } from 'antd/lib/select';
import { FilePptOutlined, SearchOutlined } from '@ant-design/icons';
import InvoiceStore from '../stores/InvoiceStore';

const { Option, OptGroup } = Select;
const SEARCH_VALUE_SEPARATOR = ':';

interface ISearchDate {
    quotesSearched: QuoteProps[];
    salesOrderSearched: SalesOrderProps[];
    invoicesSearched: IInvoice[];
}

type SearchBarProps = React.ComponentProps<typeof Select>;

const SearchBar = (props: SearchBarProps) => {
    const [query, setQuery] = useState<string | null>(null);

    const [searchData, setSearchData] = useState<ISearchDate>({
        quotesSearched: [],
        salesOrderSearched: [],
        invoicesSearched: [],
    });
    const [loading, setLoading] = useState(false);

    const history = useHistory();

    useEffect(() => {
        if (query) {
            debouncedSearch(query);
        }
    }, [query]);

    const debouncedSearch = useCallback(
        _.debounce(search => searchResults(search), 600),
        []
    );

    const searchResults = async (search: string | null) => {
        setLoading(true);

        const quoteResp = await QuoteStore.getQuotesPreview({
            search: search,
            include_quoting: true,
            // is_dynamic: false,
            limit: 5,
        });

        const salesResp = await SalesOrderStore.getAllOrders({
            search: search,
            limit: 5,
        });

        const invoices = await InvoiceStore.getInvoiceList({
            search: search,
            limit: 5,
        });

        setSearchData({
            quotesSearched: quoteResp,
            salesOrderSearched: salesResp,
            invoicesSearched: invoices.results,
        });

        setLoading(false);
    };

    return (
        <Select
            allowClear
            showSearch
            suffixIcon={!loading && <SearchOutlined style={{ fontSize: 15 }} />}
            labelInValue
            placeholder="Search"
            onSelect={value => {
                const item = value as LabeledValue;
                const [type, id] = String(item.value).split(SEARCH_VALUE_SEPARATOR);

                if (type == 'quote' && id) {
                    history.push('/my-account/quotes/' + id);
                }

                if (type == 'order' && id) {
                    history.push('/my-account/orders/' + id);
                }

                if (type == 'invoice' && id) {
                    history.push('/my-account/details-invoice/' + id);
                }
            }}
            onSearch={search => {
                setQuery(search);
            }}
            className={'mx-2'}
            onInputKeyDown={e => {
                if (e.key == 'Enter') {
                    setSearchData({
                        quotesSearched: [],
                        salesOrderSearched: [],
                        invoicesSearched: [],
                    });
                    history.push(`/my-account/search/?query=${query}`);
                }
            }}
            style={{
                width: 300,
                fontSize: 14,
            }}
            size="large"
            optionLabelProp="label"
            notFoundContent={
                loading && query ? <Spin size="small" /> : <div>Enter the Quote, Order or PO Number...</div>
            }
            filterOption={false}
            loading={loading}
            {...props}
        >
            {searchData.quotesSearched.length > 0 && (
                <OptGroup label="Quotes:" key="quotes">
                    {searchData.quotesSearched.map(item => {
                        return (
                            <Option
                                className={'quote'}
                                key={`quote-${item.id}`}
                                value={`quote:${item.id}`}
                                style={{ fontSize: 13 }}
                                label={item.name}
                            >
                                <div style={{ display: 'flex' }}>
                                    <FilePptOutlined style={{ fontSize: 30, marginTop: 5, marginRight: 5 }} />
                                    <Divider type="vertical" style={{ fontSize: 45 }} />
                                    <div
                                        style={{
                                            display: 'flex',
                                            flexDirection: 'column',
                                        }}
                                    >
                                        <div>{item.name}</div>
                                        <div
                                            style={{
                                                color: item.completed ? '#4BB543' : 'black',
                                            }}
                                        >
                                            <>({item.completed ? 'Completed' : 'Quoting'})</>
                                        </div>
                                    </div>
                                </div>
                            </Option>
                        );
                    })}
                </OptGroup>
            )}
            {searchData.salesOrderSearched.length > 0 && (
                <OptGroup label="Orders:" key={'orders'}>
                    {searchData.salesOrderSearched.map(item => {
                        return (
                            <Option
                                className="order"
                                key={`order-${item.id}`}
                                value={`order:${item.id}`}
                                style={{ fontSize: 13 }}
                                label={item.name}
                            >
                                <div style={{ display: 'flex' }}>
                                    <FilePptOutlined style={{ fontSize: 30, marginTop: 5, marginRight: 5 }} />

                                    <Divider type="vertical" style={{ fontSize: 45 }} />

                                    <div style={{ display: 'flex', flexDirection: 'column' }}>
                                        <div>
                                            <span>{item.name}</span>
                                        </div>

                                        <div>
                                            <span>{item.purchase_order}</span>
                                        </div>

                                        <div>
                                            <span
                                                style={{
                                                    color: item.intake_status == 'PAID' ? 'red' : 'inherit',
                                                }}
                                            >
                                                ({item.customer_status})
                                            </span>
                                        </div>
                                    </div>
                                </div>
                            </Option>
                        );
                    })}
                </OptGroup>
            )}
            {searchData.invoicesSearched.length > 0 && (
                <OptGroup label="Invoices:" key={'invoice'}>
                    {searchData.invoicesSearched.map(item => {
                        return (
                            <Option
                                className="invoice"
                                key={`invoice-${item.id}`}
                                value={`invoice:${item.id}`}
                                style={{ fontSize: 13 }}
                                label={item.name}
                            >
                                <div style={{ display: 'flex' }}>
                                    <FilePptOutlined style={{ fontSize: 30, marginTop: 5, marginRight: 5 }} />

                                    <Divider type="vertical" style={{ fontSize: 45 }} />

                                    <div style={{ display: 'flex', flexDirection: 'column' }}>
                                        <div>
                                            <span>{item.invoice_number}</span>
                                        </div>

                                        <div>
                                            <span>{item.purchase_order}</span>
                                        </div>

                                        <div>
                                            <span>({item.invoice_status})</span>
                                        </div>
                                    </div>
                                </div>
                            </Option>
                        );
                    })}
                </OptGroup>
            )}
            {!loading &&
                query &&
                searchData.salesOrderSearched.length === 0 &&
                searchData.quotesSearched.length == 0 &&
                searchData.invoicesSearched.length == 0 && (
                    <Select.Option disabled key={'no-results'}>
                        No results found.
                    </Select.Option>
                )}
        </Select>
    );
};

export default SearchBar;
